/**
 * FeedbackTable - Organism component for displaying paginated feedback lists
 */
import React, { useState, useMemo } from 'react';
import FeedbackCard from '../molecules/FeedbackCard';
import { FeedbackStatusType } from '../atoms/FeedbackStatus';
import { FeedbackPriorityType } from '../molecules/FeedbackCard';

interface UserSummary {
  auth0_user_id: string;
  name?: string;
  email?: string;
  picture?: string;
}

interface AppSummary {
  client_id: string;
  name: string;
  description?: string;
  logo_uri?: string;
}

interface FeedbackData {
  id: number;
  title: string;
  description: string;
  status: FeedbackStatusType;
  priority: FeedbackPriorityType;
  created_at: string;
  updated_at?: string;
  user?: UserSummary;
  app?: AppSummary;
  assigned_to?: string;
  assigned_user?: UserSummary;
  external_id?: string;
  source_url?: string;
  extra_data?: any;
}

interface FeedbackTableProps {
  feedbackList: FeedbackData[];
  total?: number;
  loading?: boolean;
  error?: string;
  onFeedbackClick?: (feedback: FeedbackData) => void;
  onPageChange?: (page: number) => void;
  onFilterChange?: (filters: FeedbackFilters) => void;
  onSortChange?: (sortBy: SortOption) => void;
  currentPage?: number;
  pageSize?: number;
  totalPages?: number;
  showFilters?: boolean;
  showPagination?: boolean;
  compact?: boolean;
  className?: string;
}

interface FeedbackFilters {
  status?: FeedbackStatusType;
  priority?: FeedbackPriorityType;
  app_id?: string;
  assigned_to?: string;
  search?: string;
}

interface SortOption {
  field: 'created_at' | 'updated_at' | 'priority' | 'status';
  direction: 'asc' | 'desc';
}

const FeedbackTable: React.FC<FeedbackTableProps> = ({
  feedbackList,
  total = 0,
  loading = false,
  error,
  onFeedbackClick,
  onPageChange,
  onFilterChange,
  onSortChange,
  currentPage = 1,
  pageSize = 10,
  totalPages = 1,
  showFilters = true,
  showPagination = true,
  compact = false,
  className = ''
}) => {
  const [filters, setFilters] = useState<FeedbackFilters>({});
  const [sort, setSort] = useState<SortOption>({ field: 'created_at', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const uniqueApps = Array.from(new Set(feedbackList.map(f => f.app?.name).filter(Boolean)));
    const uniqueAssignees = Array.from(new Set(feedbackList.map(f => f.assigned_user?.name).filter(Boolean)));
    
    return {
      apps: uniqueApps,
      assignees: uniqueAssignees
    };
  }, [feedbackList]);

  const handleFilterChange = (newFilters: Partial<FeedbackFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFilterChange?.(updatedFilters);
  };

  const handleSortChange = (newSort: SortOption) => {
    setSort(newSort);
    onSortChange?.(newSort);
  };

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    handleFilterChange({ search });
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    onFilterChange?.({});
  };

  const getPaginationInfo = () => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, total);
    return { start, end };
  };

  const { start, end } = getPaginationInfo();

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <div className="text-red-600 dark:text-red-400 font-medium mb-2">
          Error loading feedback
        </div>
        <div className="text-red-500 dark:text-red-300 text-sm">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with Count and Search */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-sm font-medium text-gray-500 dark:text-gray-400" data-testid="feedback-count">
          {total > 0 ? `${start}-${end} of ${total}` : 'No feedback available'}
        </span>

        {showFilters && (
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Search feedback..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-56 sm:w-72 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <select
            value={filters.status || ''}
            onChange={(e) => handleFilterChange({ status: e.target.value as FeedbackStatusType || undefined })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="">All Status</option>
            <option value="new">New</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
            <option value="archived">Archived</option>
          </select>

          <select
            value={filters.priority || ''}
            onChange={(e) => handleFilterChange({ priority: e.target.value as FeedbackPriorityType || undefined })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="">All Priority</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <select
            value={filters.app_id || ''}
            onChange={(e) => handleFilterChange({ app_id: e.target.value || undefined })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="">All Apps</option>
            {filterOptions.apps.map(app => (
              <option key={app} value={app}>{app}</option>
            ))}
          </select>

          <select
            value={sort.field}
            onChange={(e) => handleSortChange({ ...sort, field: e.target.value as SortOption['field'] })}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="created_at">Sort by Created</option>
            <option value="updated_at">Sort by Updated</option>
            <option value="priority">Sort by Priority</option>
            <option value="status">Sort by Status</option>
          </select>

          <button
            onClick={() => handleSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
            className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            {sort.direction === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" role="status" aria-label="Loading"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading feedback...</span>
        </div>
      )}

      {/* Feedback List */}
      {!loading && (
        <div className="space-y-4">
          {feedbackList.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400" data-testid="empty-state">
              <div className="text-4xl mb-2">📝</div>
              <div className="text-lg font-medium mb-1">No feedback found</div>
              <div className="text-sm">Try adjusting your filters or search terms</div>
            </div>
          ) : (
            feedbackList.map((feedback) => (
              <FeedbackCard
                key={feedback.id}
                feedback={feedback}
                onClick={onFeedbackClick}
                compact={compact}
                className="transition-all hover:shadow-lg"
              />
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {showPagination && totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange?.(currentPage - 1)}
              disabled={currentPage === 1}
              className="inline-flex items-center h-10 px-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange?.(pageNum)}
                    className={`flex items-center justify-center min-w-[2.5rem] h-10 px-2 text-sm font-medium rounded-lg border ${
                      currentPage === pageNum
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => onPageChange?.(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="inline-flex items-center h-10 px-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Page {currentPage} of {totalPages}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackTable; 